import { tabsContext$ } from '@pages/AdditionalServices/containers/widgetTabsContext';
import { SEARCH_PARAMS } from '@pages/consts';
import { filesService } from '@services/requests/filesService';
import { locationsBranchService } from '@services/requests/routeCalculator/locationsBranchSearchService';
import { BranchItem } from '@services/requests/routeCalculator/locationsBranchSearchService/interfaces';
import { routesCalculationService } from '@services/requests/routeCalculator/routesCalculationService';
import {
  FilterData,
  SortData,
} from '@services/requests/routeCalculator/routesCalculationService/interfaces';
import {
  AggregationData,
  RouteWidgetResult,
} from '@services/requests/routeCalculator/routesCalculationService/types';
import { terminalByLocationLoader } from '@services/requests/routeCalculator/TerminalByLocationLoader';
import { routeCalculatorSettingsService } from '@services/requests/routeCalculatorSettingsService';
import { RouteCalculatorSettings } from '@services/requests/routeCalculatorSettingsService/queries/LoadRouteCalculatorSettingsQuery';
import { allowanceLoader } from '@services/requests/searchLoaders/allowanceLoader';
import { carriersLoader } from '@services/requests/searchLoaders/carriersLoader';
import { containersLoader } from '@services/requests/searchLoaders/containersLoader';
import { containerTypeLoader } from '@services/requests/searchLoaders/containerTypesLoader';
import { contractorLoader } from '@services/requests/searchLoaders/contractorLoader';
import { currencyLoader } from '@services/requests/searchLoaders/currencyLoader';
import { endTransportingConditionsLoader } from '@services/requests/searchLoaders/endTransportingConditionsLoader';
import { loadUnloadConditionsLoader } from '@services/requests/searchLoaders/LoadUnloadConditionsLoader';
import { shoulderTypesLoader } from '@services/requests/searchLoaders/shouldeTypesLoader';
import { startTransportingConditionsLoader } from '@services/requests/searchLoaders/startTransportingConditionsLoader';
import { trucksLoader } from '@services/requests/searchLoaders/trucksLoader';
import { unitGroupsLoader } from '@services/requests/searchLoaders/unitGroupsLoader';
import { unitLoader } from '@services/requests/searchLoaders/unitLoader';
import { shortLinkService } from '@services/requests/shortLinkService';
import { taxLoader } from '@services/requests/taxLoader';
import { useStateSubscriber } from '@settings/core/stateContexts/useContextSubscriber';
import { $error } from '@settings/errorContext';
import { LoadCollection } from '@settings/services/helpers/LoadCollection';
import dayjs from 'dayjs';
import { BehaviorSubject, distinctUntilChanged, filter, map, pairwise, withLatestFrom } from 'rxjs';
import { deleteSearchParams, getValueFromSearchParamsByKey } from 'src/utils/search-params';

import {
  RouteSearchTaskStatusDoneResponse,
  RouteSearchTaskStatusUpdateResponse,
} from './components/RoutesListComponent/RoutesCalculationLoader/types';
import DELIVERY_FORM_CONSTANTS, {
  ONLY_IDS_ROUTE_PARAMS_KEYS,
  SHARE_SHORT_LINK_KEY,
} from './consts';
import { DefaultRouteSearchTaskStatus } from './DefaultRouteSearchTaskStatus';
import {
  checkEqualityTransportWithCustomWeights,
  getShareCalculationParams,
  isRouteSearchTaskStatusDonePredicate,
  isShareParamsEqual,
} from './helpers';
import {
  CargoType,
  RouteSearchTaskStatus,
  RouteWidgetBaseParameters,
  RouteWidgetParams,
} from './types';

const LOCALSTORAGE_CONTEXT_KEY = '__ONLOG_ROUTE_WIDGET__';
const LOCALSTORAGE_UPDATE_CALCULATION_KEY = 'ONLOG_ROUTE_WIDGET';

interface Settings {
  primaryLanguageId: string;
  secondaryLanguageId: string;
  currencyId: string;
}

const $isLoading = new BehaviorSubject<boolean>(false);
const $isInit = new BehaviorSubject<boolean>(false);
const $settings = new BehaviorSubject<Settings>(DELIVERY_FORM_CONSTANTS.settings);
const $deliverySearchParams = new BehaviorSubject<RouteCalculatorSettings | null>(null);
const $baseData = new BehaviorSubject<RouteWidgetBaseParameters | undefined>(undefined);
const $isNeedRoutesReloading = new BehaviorSubject<boolean>(false);
const $fromBranch = new BehaviorSubject<BranchItem | undefined>(undefined);
const $toBranch = new BehaviorSubject<BranchItem | undefined>(undefined);
const $routeParams = new BehaviorSubject<RouteWidgetParams>(DELIVERY_FORM_CONSTANTS.routeParams);
const $isConfigFromSearchParams = new BehaviorSubject<boolean>(false);
const $aggregationData = new BehaviorSubject<AggregationData | undefined>(undefined);
const $routes = new BehaviorSubject<RouteWidgetResult>(DELIVERY_FORM_CONSTANTS.routes);
const $terminals = new BehaviorSubject<BranchItem[]>([]);
const $startTerminals = new BehaviorSubject<BranchItem[]>([]);
const $endTerminals = new BehaviorSubject<BranchItem[]>([]);
const $filterParameters = new BehaviorSubject<FilterData>(DELIVERY_FORM_CONSTANTS.filterParameters);
const $cacheFilterParameters = new BehaviorSubject<FilterData>(
  DELIVERY_FORM_CONSTANTS.filterParameters
);
const $isArchiveLoading = new BehaviorSubject<boolean>(false);
const $isMoreRoutesLoadingAvailable = new BehaviorSubject<boolean>(false);
const $isRoutesLoading = new BehaviorSubject<boolean>(false);
const $isMoreRoutesLoading = new BehaviorSubject<boolean>(false);
const $sortData = new BehaviorSubject<SortData>(DELIVERY_FORM_CONSTANTS.sortData);
const $limit = new BehaviorSubject<number>(DELIVERY_FORM_CONSTANTS.limit);
const $offset = new BehaviorSubject<number>(DELIVERY_FORM_CONSTANTS.offset);
const $validation = new BehaviorSubject<boolean>(true);
const $routeSearchTaskId = new BehaviorSubject<string | undefined>(undefined);
const $routeSearchTaskStatus = new BehaviorSubject<RouteSearchTaskStatus>(
  new DefaultRouteSearchTaskStatus()
);
const $isNeedFullReload = new BehaviorSubject<boolean>(false);
const $calculationAbortController = new BehaviorSubject<AbortController>(new AbortController());
const $isEditCalculationParametersActive = new BehaviorSubject<boolean>(false);
const $isTransportWithCustomWeightDiff = new BehaviorSubject<boolean>(false);
const $isStartSearchWithWeightDiff = new BehaviorSubject<boolean>(false);

$routeParams
  .pipe(
    map((state) => state.customCargoParameters),
    pairwise(),
    filter(([prev, curr]) => {
      const firstWriteHappened = prev.cargoFingerprint === '' && curr.cargoFingerprint !== '';
      if (firstWriteHappened) return false;

      const prevTransportTypes = prev.transportTypes;
      const currTransportTypes = curr.transportTypes;

      const weightEqual = (a: number, b: number) => (Number.isNaN(a) && Number.isNaN(b)) || a === b;

      if (prevTransportTypes.length !== currTransportTypes.length) {
        return true;
      }
      return !prevTransportTypes.every(
        (item, index) =>
          currTransportTypes[index].type === item.type &&
          weightEqual(currTransportTypes[index].weight, item.weight) &&
          currTransportTypes[index].transportTypeId === item.transportTypeId
      );
    })
  )
  .subscribe({
    next: ([_, curr]) => {
      const fullState = $routeParams.getValue();
      $routeParams.next({
        ...fullState,
        customCargoParameters: {
          ...curr,
          cargoFingerprint: '',
        },
      });
    },
  });

$routeParams
  .pipe(
    distinctUntilChanged(
      (prev, curr) =>
        prev.startLocation === curr.startLocation &&
        prev.endLocation === curr.endLocation &&
        prev.cargoType === curr.cargoType
    )
  )
  .pipe(withLatestFrom($isInit))
  .subscribe({
    next: ([state, isInit]) => {
      if (!isInit) {
        return;
      }
      $routeParams.next({
        ...state,
        containerParameters: {
          ...state.containerParameters,
          containerParameters: state.containerParameters.containerParameters.map((c) => ({
            ...c,
            withPallets: false,
            palletsLoaded: [],
          })),
        },
        truckParameters: {
          ...state.truckParameters,
          truckParameters: state.truckParameters.truckParameters.map((t) => ({
            ...t,
            withPallets: false,
            palletsLoaded: [],
          })),
        },
        isNeedPrekeridge: false,
        isNeedContainerRent: true,
        isWithoutPrekeridge: false,
        isWithoutOnkeridge: false,
        isFromTerminalRadius: false,
        isToTerminalRadius: false,
        isDangerCargo: false,
        isWithCrossDocking: false,
      });
    },
  });

const init = async (primaryLanguageId: string, secondaryLanguageId: string, currencyId: string) => {
  $isLoading.next(true);
  $settings.next({
    primaryLanguageId,
    secondaryLanguageId,
    currencyId,
  });

  const deliverySearchParams = await routeCalculatorSettingsService().Load();

  const partOfBaseData = await LoadCollection<Omit<RouteWidgetBaseParameters, 'files'>>({
    currencies: currencyLoader().Load(),
    containers: containersLoader().Load(),
    trucks: trucksLoader().Load(),
    containerTypes: containerTypeLoader().Load(),
    endTransportingConditions: endTransportingConditionsLoader().Load(),
    startTransportingConditions: startTransportingConditionsLoader().Load(),
    carriers: carriersLoader().Load(),
    contractors: contractorLoader().Load(),
    shoulderTypes: shoulderTypesLoader().Load(),
    allowancesData: allowanceLoader().Load(),
    units: unitLoader().Load(),
    unitGroups: unitGroupsLoader().Load(),
    loadingTransportConditions: loadUnloadConditionsLoader().getLoadingConditions(),
    unloadingTransportConditions: loadUnloadConditionsLoader().getUnloadingConditions(),
    taxes: taxLoader().Load(),
  });

  // Получаем список id файлов для перевозчиков и подрядчиков
  const filesIdsList = [...partOfBaseData.contractors, ...partOfBaseData.carriers]
    .map((item) => item.files)
    .flat();

  const baseData: RouteWidgetBaseParameters = {
    ...partOfBaseData,
    files: await filesService().LoadFilesById(filesIdsList),
  };

  const getValueRouteParams = $routeParams.getValue();

  const contextRouteParams: RouteWidgetParams = {
    ...getValueRouteParams,
    containerParameters: {
      ...getValueRouteParams.containerParameters,
      containerParameters: [
        (await containersLoader().Load()).map((item) => ({
          containerId: null,
          containerType: item.containerType.id.toString(),
          containerQuantity:
            getValueRouteParams.containerParameters.containerParameters[0].containerQuantity,
          eachContainerWeight: item.default_weight,
          withPallets: false,
          palletsLoaded: [],
        }))[2],
      ],
    },
    truckParameters: {
      ...getValueRouteParams.truckParameters,
      truckParameters: getValueRouteParams.truckParameters.truckParameters.map((param) => ({
        ...param,
        truckId: partOfBaseData.trucks[0].id,
        withPallets: false,
        palletsLoaded: [],
      })),
    },
  };

  const shortLink = getValueFromSearchParamsByKey(SHARE_SHORT_LINK_KEY);

  const shortLinkData = await shortLinkService().LoadShortLink({ id: shortLink });

  let parsedShortLinkData = {};
  if (shortLinkData?.data) {
    parsedShortLinkData = JSON.parse(shortLinkData?.data ?? '{}');
  }

  const [isRouteSearchParamsValid, parsedRouteParams] =
    getShareCalculationParams(parsedShortLinkData);

  let routeParams = { ...contextRouteParams };

  if (isRouteSearchParamsValid) {
    $isConfigFromSearchParams.next(true);

    const {
      id: startId,
      parentId: startParentId,
      ...restStartLocationParams
    } = parsedRouteParams.startLocation;
    const {
      id: endId,
      parentId: endParentId,
      ...restEndLocationParams
    } = parsedRouteParams.endLocation;

    const startLocationId = parsedRouteParams.startLocation.id;
    const endLocationId = parsedRouteParams.endLocation.id;
    const startLocationIds = Object.values([startId, startParentId]);
    const endLocationIds = Object.values([endId, endParentId]);

    const { startLocations, endLocations } = await LoadCollection<{
      startLocations: BranchItem[];
      endLocations: BranchItem[];
    }>({
      startLocations: terminalByLocationLoader().Load(startLocationIds, [startLocationId]),
      endLocations: terminalByLocationLoader().Load(endLocationIds, [endLocationId]),
    });

    const startSearchResult = startLocations.map((l) => ({
      id: l.id,
      type: l.type,
    }));
    startSearchResult.push({ id: startLocationId, type: 'location' });

    const endSearchResult = endLocations.map((l) => ({
      id: l.id,
      type: l.type,
    }));
    endSearchResult.push({ id: endLocationId, type: 'location' });

    await Promise.all([
      locationsBranchService().getLocationWithParentTree(
        startSearchResult,
        await locationsBranchService().loadLocalization(startLocations)
      ),
      locationsBranchService().getLocationWithParentTree(
        endSearchResult,
        await locationsBranchService().loadLocalization(endLocations)
      ),
    ]);

    const foundStartLocation = startLocations.find((location) => location.id === startLocationId);
    const foundEndLocation = endLocations.find((location) => location.id === endLocationId);

    const params = {
      ...parsedRouteParams,
      containerParameters: {
        ...parsedRouteParams.containerParameters,
        containerParameters: parsedRouteParams.containerParameters.containerParameters.map(
          (container) => ({
            ...container,
            withPallets: container.withPallets ?? false,
            palletsLoaded: container.palletsLoaded ?? [],
          })
        ),
      },
      truckParameters: parsedRouteParams.truckParameters
        ? {
            ...parsedRouteParams.truckParameters,
            truckParameters: parsedRouteParams.truckParameters.truckParameters.map((truck) => ({
              ...truck,
              withPallets: truck.withPallets ?? false,
              palletsLoaded: truck.palletsLoaded ?? [],
            })),
          }
        : {
            ...DELIVERY_FORM_CONSTANTS.routeParams.truckParameters,
            truckParameters:
              DELIVERY_FORM_CONSTANTS.routeParams.truckParameters.truckParameters.map((tp) => ({
                ...tp,
                truckId: baseData.trucks[0].id,
              })),
          },
      startLocation: { ...foundStartLocation, ...restStartLocationParams },
      endLocation: { ...foundEndLocation, ...restEndLocationParams },
    } as RouteWidgetParams;

    routeParams = {
      ...params,
    };
  } else {
    if (!routeParams.isDangerCargo) {
      routeParams.isDangerCargo =
        routeParams.containerParameters.isDangerousCargo ||
        routeParams.customCargoParameters.isDangerousCargo ||
        routeParams.truckParameters.isDangerousCargo ||
        false;
    }

    resetSearchParams();

    try {
      const updateCalculationParams = JSON.parse(
        localStorage.getItem(LOCALSTORAGE_UPDATE_CALCULATION_KEY) || ''
      );
      if (updateCalculationParams) {
        const { containerParameters, customCargoParameters, truckParameters } =
          updateCalculationParams.data.cargoParameters;

        const containerCargoParams = JSON.parse(containerParameters || 'null');
        const truckCargoParams = JSON.parse(truckParameters || 'null');
        const customCargoParams = JSON.parse(customCargoParameters || 'null');

        const { from, to } =
          containerCargoParams?.routePoints ||
          truckCargoParams?.routePoints ||
          customCargoParams?.routePoints ||
          null;

        const {
          startTransportingConditionId,
          endTransportingConditionId,
          requiredTransportingTypes,
          isNeedPrekeridge,
          isNeedContainerRent,
          isDangerousCargo,
          isWithoutPrekeridge = false,
          isWithoutOnkeridge = false,
          isFromTerminalRadius = false,
          isToTerminalRadius = false,
          isWithCrossDocking = false,
        } = containerCargoParams || truckCargoParams || customCargoParams || {};

        const fromLocationIds = Object.values(from)
          .filter(Boolean)
          .map((location) => String(location));
        const toLocationIds = Object.values(to)
          .filter(Boolean)
          .map((location) => String(location));

        const fromLocationType = from.location !== 0 ? 'location' : 'terminal';
        const toLocationType = to.location !== 0 ? 'location' : 'terminal';

        const fromLocation = fromLocationIds[0];
        const toLocation = toLocationIds[0];

        const { startLocations, endLocations } = await LoadCollection<{
          startLocations: BranchItem[];
          endLocations: BranchItem[];
        }>({
          startLocations: terminalByLocationLoader().Load(fromLocationIds, [fromLocation]),
          endLocations: terminalByLocationLoader().Load(toLocationIds, [toLocation]),
        });

        const startLocation = startLocations.find((location) => location.id === fromLocation);
        const endLocation = endLocations.find((location) => location.id === toLocation);

        if (startLocation?.type === 'location') {
          startLocations.push({ ...startLocation, isWarehouse: false });
          startLocations.push({ ...startLocation, isWarehouse: true });
        }

        if (endLocation?.type === 'location') {
          endLocations.push({ ...endLocation, isWarehouse: false });
          endLocations.push({ ...endLocation, isWarehouse: true });
        }

        const startItem = await locationsBranchService().GetItemByValue(
          fromLocation,
          fromLocationType
        );
        if (startTransportingConditionId === 3) {
          startItem.isWarehouse = true;
        } else {
          startItem.isWarehouse = false;
        }

        const endItem = await locationsBranchService().GetItemByValue(toLocation, toLocationType);
        if (endTransportingConditionId === 3) {
          endItem.isWarehouse = true;
        } else {
          endItem.isWarehouse = false;
        }

        if (startLocations.length === 0) {
          startLocations.push(startItem);
        }

        if (endLocations.length === 0) {
          startLocations.push(endItem);
        }

        const startSearchResult = startLocations.map((l) => ({
          id: l.id,
          type: l.type,
        }));
        startSearchResult.push({ id: fromLocation, type: 'location' });

        const endSearchResult = endLocations.map((l) => ({
          id: l.id,
          type: l.type,
        }));
        endSearchResult.push({ id: toLocation, type: 'location' });

        const [filledStartLocations, filledEndLocations] = await Promise.all([
          locationsBranchService().getLocationWithParentTree(
            startSearchResult.reduce((prev, item) => {
              const foundDuplicate = prev.find((i) => item.id === i.id && item.type === i.type);

              if (foundDuplicate) {
                return prev;
              }
              return [...prev, item];
            }, []),
            await locationsBranchService().loadLocalization(startLocations)
          ),
          locationsBranchService().getLocationWithParentTree(
            endSearchResult.reduce((prev, item) => {
              const foundDuplicate = prev.find((i) => item.id === i.id && item.type === i.type);

              if (foundDuplicate) {
                return prev;
              }
              return [...prev, item];
            }, []),
            await locationsBranchService().loadLocalization(endLocations)
          ),
        ]);

        const foundStartLocation = filledStartLocations.find(
          (location) => location.id === fromLocation
        );
        const foundEndLocation = filledEndLocations.find((location) => location.id === toLocation);

        if (startTransportingConditionId === 3) {
          foundStartLocation.isWarehouse = true;
          foundStartLocation.visibleName = 'ShippersWarehouse';
          foundStartLocation.main = true;
        } else if (foundStartLocation.type === 'location' && startTransportingConditionId !== 3) {
          foundStartLocation.visibleName = 'CarrierTerminal';
          foundStartLocation.main = true;
          foundStartLocation.type = 'terminal';
          foundStartLocation.symbolCode = 'ANY';
        }

        if (endTransportingConditionId === 3) {
          foundEndLocation.isWarehouse = true;
          foundEndLocation.visibleName = 'ConsigneesWarehouse';
          foundEndLocation.main = true;
        } else if (foundEndLocation.type === 'location' && endTransportingConditionId !== 3) {
          foundEndLocation.visibleName = 'CarrierTerminal';
          foundEndLocation.main = true;
          foundEndLocation.type = 'terminal';
          foundEndLocation.symbolCode = 'ANY';
        }

        let cargoType = !!containerCargoParams ? 'container' : 'truck';

        if (!!customCargoParams) {
          cargoType = 'custom';
        }

        routeParams = {
          cargoType: cargoType as CargoType,
          truckParameters: truckCargoParams
            ? {
                endTransportingConditionId: truckCargoParams.endTransportingConditionId.toString(),
                isCustomConditionsOpen: truckCargoParams.isDangerousCargo,
                isDangerousCargo: truckCargoParams.isDangerousCargo,
                startTransportingConditionId:
                  truckCargoParams.startTransportingConditionId.toString(),
                targetCurrencyId: truckCargoParams.targetCurrencyId,
                truckParameters: [
                  {
                    truckId: truckCargoParams.truckId,
                    truckQuantity: truckCargoParams.truckQuantity,
                    eachTruckWeight: truckCargoParams.eachTruckWeight,
                    withPallets: truckCargoParams.withPallets ?? false,
                    palletsLoaded: truckCargoParams.palletsLoaded ?? [],
                  },
                ],
              }
            : {
                ...DELIVERY_FORM_CONSTANTS.routeParams.truckParameters,
                truckParameters:
                  DELIVERY_FORM_CONSTANTS.routeParams.truckParameters.truckParameters.map(
                    (truck) => ({ ...truck, truckId: partOfBaseData.trucks[0].id })
                  ),
              },
          containerParameters: containerCargoParams
            ? {
                endTransportingConditionId:
                  containerCargoParams.endTransportingConditionId.toString(),
                isCustomConditionsOpen: containerCargoParams.isDangerousCargo,
                isDangerousCargo: containerCargoParams.isDangerousCargo,
                startTransportingConditionId:
                  containerCargoParams.startTransportingConditionId.toString(),
                targetCurrencyId: containerCargoParams.targetCurrencyId,
                containerParameters: [
                  {
                    containerId: containerCargoParams.containerId,
                    containerQuantity: containerCargoParams.containerQuantity,
                    containerType: '1',
                    eachContainerWeight: containerCargoParams.eachContainerWeight,
                    withPallets: containerCargoParams.withPallets ?? false,
                    palletsLoaded: containerCargoParams.palletsLoaded ?? [],
                  },
                ],
              }
            : {
                ...DELIVERY_FORM_CONSTANTS.routeParams.containerParameters,
                containerParameters:
                  DELIVERY_FORM_CONSTANTS.routeParams.containerParameters.containerParameters.map(
                    (container) => ({ ...container, containerId: '1' })
                  ),
              },
          customCargoParameters: customCargoParams
            ? {
                customCargoParameters: [
                  {
                    baseParameters: customCargoParams.baseParameters,
                    palletParameters: customCargoParams.palletParameters,
                    volumeParameters: customCargoParams.volumeParameters,
                    parametersType: customCargoParams.parametersType,
                    parametersCargoCalcType: customCargoParams.parametersType,
                  },
                ],
                endTransportingConditionId: customCargoParams.endTransportingConditionId.toString(),
                isCustomConditionsOpen: customCargoParams.isDangerousCargo,
                isDangerousCargo: customCargoParams.isDangerousCargo,
                startTransportingConditionId:
                  customCargoParams.startTransportingConditionId.toString(),
                targetCurrencyId: customCargoParams.targetCurrencyId,
                cargoFingerprint: customCargoParams.cargoFingerprint ?? '',
                transportTypes: customCargoParams.transportTypes ?? [],
              }
            : { ...DELIVERY_FORM_CONSTANTS.routeParams.customCargoParameters },
          startExpeditionDate: updateCalculationParams.date,
          isDangerCargo: isDangerousCargo,
          requiredTransportingTypes: requiredTransportingTypes,
          endTransportingCondition: endTransportingConditionId.toString(),
          startTransportingCondition: startTransportingConditionId.toString(),
          endLocation: foundEndLocation,
          startLocation: foundStartLocation,
          isNeedContainerRent: isNeedContainerRent,
          isNeedPrekeridge: isNeedPrekeridge,
          isWithoutPrekeridge: isWithoutPrekeridge,
          isWithoutOnkeridge: isWithoutOnkeridge,
          isFromTerminalRadius: isFromTerminalRadius,
          isToTerminalRadius: isToTerminalRadius,
          isWithCrossDocking: isWithCrossDocking,
        };
      } else {
        routeParams = JSON.parse(localStorage.getItem(LOCALSTORAGE_CONTEXT_KEY) || '');

        if (!routeParams) {
          routeParams = contextRouteParams;
        }
      }
    } catch (e) {}
  }

  routeParams.startExpeditionDate = new Date(routeParams.startExpeditionDate || new Date());

  const startExpeditionDate = routeParams.startExpeditionDate;
  const startOfCurrentDayDate = dayjs().startOf('day').toDate();

  if (startExpeditionDate && startExpeditionDate < startOfCurrentDayDate) {
    routeParams.startExpeditionDate = dayjs().toDate();
  }

  const defaultStartTransportingCondition = baseData.startTransportingConditions.find(
    (condition) => condition.id === '1'
  );
  const defaultEndTransportingCondition = baseData.endTransportingConditions.find(
    (condition) => condition.id === '1'
  );

  $deliverySearchParams.next(deliverySearchParams);
  $baseData.next(baseData);
  $routeParams.next({
    ...routeParams,
    truckParameters: {
      ...routeParams.truckParameters,
      targetCurrencyId: currencyId,
    },
    containerParameters: {
      ...routeParams.containerParameters,
      targetCurrencyId: currencyId,
    },
    customCargoParameters: {
      ...routeParams.customCargoParameters,
      targetCurrencyId: currencyId,
      cargoFingerprint: routeParams.customCargoParameters?.cargoFingerprint ?? '',
      transportTypes: routeParams.customCargoParameters?.transportTypes ?? [],
    },
    startTransportingCondition:
      routeParams.startTransportingCondition || defaultStartTransportingCondition.id,
    endTransportingCondition:
      routeParams.endTransportingCondition || defaultEndTransportingCondition.id,
  });
  $isLoading.next(false);

  if (currencyId) {
    $isInit.next(true);
  }
};

$routeSearchTaskStatus
  .pipe(filter(({ isSearchStepsComplete }) => !!isSearchStepsComplete))
  .subscribe({
    next: () => {
      recalculateRoutes($isNeedFullReload.getValue());
    },
  });

/** *
 * Перерасчет маршрутов
 */
const recalculateRoutes = async (isNeedFullReload: boolean) => {
  $validation.next(true);
  try {
    if (isNeedFullReload) {
      $aggregationData.next(undefined);
      $isLoading.next(true);
      $offset.next(0);
      $limit.next(10);

      await reloadFullRoute();
    } else {
      $isRoutesLoading.next(true);

      await reloadRoute();
    }
  } catch (e) {
    $isMoreRoutesLoading.next(false);
    $isLoading.next(false);
    $isRoutesLoading.next(false);
  } finally {
    $isMoreRoutesLoading.next(false);
    $isLoading.next(false);
    $isRoutesLoading.next(false);
    saveToLocalStorage();
    resetRouteSearchTaskStatus();
  }
};

const abortRouteSearch = () => {
  const calculationAbortController = $calculationAbortController.getValue();
  calculationAbortController.abort();

  $isLoading.next(false);
  $isRoutesLoading.next(false);
  $isEditCalculationParametersActive.next(false);
  $isStartSearchWithWeightDiff.next(false);
  resetRouteSearchTaskStatus();
};

export const disableEditCalculationParameters = () => {
  $isEditCalculationParametersActive.next(false);
};

const createAndRunRouteSearchTask = async (isNeedFullReload: boolean) => {
  const routeParams = $routeParams.getValue();
  const isArchiveLoading = $isArchiveLoading.getValue();
  const limit = $limit.getValue();
  const offset = $offset.getValue();
  const isStartSearchWithWeightDiff = $isStartSearchWithWeightDiff.getValue();

  if (!routeParams) {
    return;
  }

  const mayBeCalculated =
    !!routeParams.startExpeditionDate &&
    !!routeParams.startLocation &&
    !!routeParams.endLocation &&
    !!routeParams.cargoType;

  if (!mayBeCalculated) {
    $isInit.getValue() && $validation.next(false);
    return;
  }

  $routes.next(DELIVERY_FORM_CONSTANTS.routes);

  const routeCalculateParams = {
    ...routeParams,
    limit,
    offset,
  };

  const taskData = await routesCalculationService().CreateRouteSearchTask(
    routeCalculateParams,
    isArchiveLoading,
    isStartSearchWithWeightDiff
  );

  if (
    routeParams.cargoType === 'fclFtl' &&
    !checkEqualityTransportWithCustomWeights(routeParams) &&
    !isStartSearchWithWeightDiff
  ) {
    $isTransportWithCustomWeightDiff.next(true);
    return;
  }

  if (!taskData || !taskData.taskId) {
    return;
  }

  $calculationAbortController.next(new AbortController());
  $routeSearchTaskId.next(taskData.taskId);
  $isNeedFullReload.next(isNeedFullReload);
  $isEditCalculationParametersActive.next(true);
  if (isNeedFullReload) {
    $isLoading.next(true);
  } else {
    $isRoutesLoading.next(true);
  }
};

/**
 * Загрузка данных по обновленным параметрам фильтрации и сортировки
 */
const reloadRoute = async () => {
  const service = routesCalculationService();
  const routeParams = $routeParams.getValue();
  const isArchiveLoading = $isArchiveLoading.getValue();
  const filterParameters = $filterParameters.getValue();
  const limit = $limit.getValue();
  const offset = $offset.getValue();
  const sortData = $sortData.getValue();
  const taskId = $routeSearchTaskId.getValue();
  const calculationAbortController = $calculationAbortController.getValue();

  if (!routeParams) {
    return;
  }
  const mayBeCalculated =
    !!routeParams.startExpeditionDate &&
    !!routeParams.startLocation &&
    !!routeParams.endLocation &&
    !!routeParams.cargoType;

  if (!mayBeCalculated) {
    $isInit.getValue() && $validation.next(false);
    return;
  }

  const routeCalculateParams = {
    ...routeParams,
    limit,
    offset,
  };

  const calculateResult = await service.GetRoutesList(
    routeCalculateParams,
    isArchiveLoading,
    filterParameters,
    sortData,
    false,
    taskId,
    calculationAbortController,
    !checkEqualityTransportWithCustomWeights(routeParams)
  );

  if (!calculateResult) {
    return;
  }

  $fromBranch.next(routeParams.startLocation);
  $toBranch.next(routeParams.endLocation);
  $routes.next(calculateResult.routes);
  $isMoreRoutesLoadingAvailable.next(offset + 1 + 10 < calculateResult.routes.total);
};

/**
 * Полная загрузка данных по маршруту
 */
const reloadFullRoute = async () => {
  const service = routesCalculationService();
  const routeParams = $routeParams.getValue();
  const isArchiveLoading = $isArchiveLoading.getValue();
  const limit = $limit.getValue();
  const offset = $offset.getValue();
  const sortData = $sortData.getValue();
  const taskId = $routeSearchTaskId.getValue();
  const calculationAbortController = $calculationAbortController.getValue();
  const isStartSearchWithWeightDiff = $isStartSearchWithWeightDiff.getValue();

  if (!routeParams) {
    return;
  }

  const mayBeCalculated =
    !!routeParams.startExpeditionDate &&
    !!routeParams.startLocation &&
    !!routeParams.endLocation &&
    !!routeParams.cargoType;

  if (!mayBeCalculated) {
    $isInit.getValue() && $validation.next(false);
    return;
  }

  const routeCalculateParams = {
    ...routeParams,
    limit,
    offset,
  };

  const aggregationData = await service.GetRoutesAggregation(
    routeCalculateParams,
    isArchiveLoading,
    taskId,
    calculationAbortController,
    isStartSearchWithWeightDiff
  );

  const filterParameters: FilterData = {
    carriers: aggregationData?.carriers ?? [],
    contractors: aggregationData?.contractors ?? [],
    deliveryTime: {
      min: aggregationData
        ? aggregationData.deliveryTime.min > 0
          ? aggregationData.deliveryTime.min
          : 0
        : 0,
      max: aggregationData
        ? aggregationData.deliveryTime.max > 0
          ? aggregationData.deliveryTime.max
          : 0
        : 0,
    },
    price: {
      min: aggregationData ? (aggregationData.price.min > 0 ? aggregationData.price.min : 0) : 0,
      max: aggregationData ? (aggregationData.price.max > 0 ? aggregationData.price.max : 0) : 0,
    },
    transportingTypes: aggregationData?.transportingTypes ?? [],
    isSingleShoulderRoutesOnly: false,
    isNeedPrekeridgeRoutes: true,
    isNeedRegularRoutes: true,
    terminals: aggregationData?.terminals ?? [],
    startTerminals: aggregationData?.startTerminals ?? [],
    endTerminals: aggregationData?.endTerminals ?? [],
    containerRentType: aggregationData?.containerRentType ?? [],
    alternativeRoutes: aggregationData?.alternativeRoutes,
  };

  const calculateResult = await service.GetRoutesList(
    routeCalculateParams,
    isArchiveLoading,
    filterParameters,
    sortData,
    true,
    taskId,
    calculationAbortController,
    isStartSearchWithWeightDiff
  );

  if (!calculateResult) {
    return;
  }

  $isStartSearchWithWeightDiff.next(false);
  $isMoreRoutesLoadingAvailable.next(offset + 1 + 10 < calculateResult.routes.total);
  $fromBranch.next(routeParams.startLocation);
  $toBranch.next(routeParams.endLocation);
  $routes.next(calculateResult.routes);
  $terminals.next(calculateResult.terminals);
  $startTerminals.next(calculateResult.startTerminals);
  $endTerminals.next(calculateResult.endTerminals);
  $filterParameters.next(filterParameters);
  $cacheFilterParameters.next(filterParameters);
  $aggregationData.next(aggregationData);
};

/**
 * Обработчик сохранения параметров для расчета
 */
const saveToLocalStorage = () => {
  const routeParams = $routeParams.getValue();
  const isInit = $isInit.getValue();

  if (!isInit) {
    return;
  }

  try {
    localStorage.setItem(LOCALSTORAGE_CONTEXT_KEY, JSON.stringify(routeParams));
  } catch (e) {
    $error.next(e);
  }
};

const resetWidget = () => {
  $isLoading.next(false);
  $isInit.next(false);
  $baseData.next(undefined);
  $deliverySearchParams.next(null);
  $isNeedRoutesReloading.next(false);
  $routeParams.next(DELIVERY_FORM_CONSTANTS.routeParams);
  $aggregationData.next(undefined);
  $routes.next(DELIVERY_FORM_CONSTANTS.routes);
  $terminals.next([]);
  $startTerminals.next([]);
  $endTerminals.next([]);
  $filterParameters.next(DELIVERY_FORM_CONSTANTS.filterParameters);
  $cacheFilterParameters.next(DELIVERY_FORM_CONSTANTS.filterParameters);
  $isArchiveLoading.next(false);
  $sortData.next(DELIVERY_FORM_CONSTANTS.sortData);
  $limit.next(DELIVERY_FORM_CONSTANTS.limit);
  $offset.next(DELIVERY_FORM_CONSTANTS.offset);

  const { primaryLanguageId, secondaryLanguageId, currencyId } = $settings.getValue();
  $isLoading.next(true);
  init(primaryLanguageId, secondaryLanguageId, currencyId);
  $isLoading.next(false);
  localStorage.removeItem(LOCALSTORAGE_CONTEXT_KEY);
  localStorage.removeItem(LOCALSTORAGE_UPDATE_CALCULATION_KEY);
  resetSearchParams();
  resetRouteSearchTaskStatus();
  $routeSearchTaskId.next(undefined);
  $isTransportWithCustomWeightDiff.next(false);
  $isStartSearchWithWeightDiff.next(false);
};

export const getSerializedShareData = (routeParams: RouteWidgetParams) => {
  const formattedRouteSearchParams = Object.entries(routeParams);
  const shareData: Record<string, string> = {};

  for (const [key, value] of formattedRouteSearchParams) {
    if (ONLY_IDS_ROUTE_PARAMS_KEYS.includes(key)) {
      const branchItem = value as BranchItem;
      const { id, parentId, type, main, isWarehouse, symbolCode, visibleName } = branchItem;

      shareData[key] = JSON.stringify({
        id,
        parentId,
        type,
        main,
        isWarehouse,
        symbolCode,
        visibleName,
      });
    } else if (value instanceof Date) {
      shareData[key] = value.toISOString();
    } else {
      shareData[key] = JSON.stringify(value);
    }
  }
  return JSON.stringify(shareData);
};

const saveToSearchParams = async () => {
  const routeParams = $routeParams.getValue();

  const shortLink = getValueFromSearchParamsByKey(SHARE_SHORT_LINK_KEY);

  const serializedShareData = getSerializedShareData(routeParams);

  const shortLinkData = await shortLinkService().LoadShortLink({ id: shortLink });
  const isParamsEqual = isShareParamsEqual(serializedShareData, shortLinkData?.data || '{}');

  if (isParamsEqual) {
    return;
  }

  const generatedShortLink = await shortLinkService().GenerateShortLink({
    page: 'delivery',
    data: serializedShareData,
  });

  history.pushState(null, '', `?${SHARE_SHORT_LINK_KEY}=` + generatedShortLink);
};

const resetSearchParams = () => {
  const activeTab = tabsContext$.getValue();

  if (activeTab.ActiveTabData.type !== 'delivery') {
    return;
  }

  deleteSearchParams([SEARCH_PARAMS.share, SEARCH_PARAMS.editProductId]);
};

const updateRouteSearchTaskStatus = (
  newStatus: RouteSearchTaskStatusUpdateResponse | RouteSearchTaskStatusDoneResponse,
  onDoneCallback?: () => void
) => {
  const currentRouteSearchTaskStatus = $routeSearchTaskStatus.getValue();

  if (isRouteSearchTaskStatusDonePredicate(newStatus)) {
    if (newStatus.status === 'progress') {
      return;
    }
    $routeSearchTaskStatus.next({
      ...currentRouteSearchTaskStatus,
      isSearchStepsComplete: true,
    });
    onDoneCallback && onDoneCallback();
    return;
  }

  const { taskId, ...restStatus } = newStatus;

  $routeSearchTaskStatus.next({
    ...currentRouteSearchTaskStatus,
    ...restStatus,
  });
};

const resetRouteSearchTaskStatus = () => {
  $routeSearchTaskStatus.next(new DefaultRouteSearchTaskStatus());
};

const actions = {
  saveToLocalStorage,
  resetWidget,
  recalculateRoutes,
  resetSearchParams,
  saveToSearchParams,
  init,
  updateRouteSearchTaskStatus,
  createAndRunRouteSearchTask,
  resetRouteSearchTaskStatus,
  abortRouteSearch,
  getSerializedShareData,
};

const useDelivery = () => {
  const useIsLoading = () => useStateSubscriber<boolean>($isLoading);
  const useBaseDate = () => useStateSubscriber<RouteWidgetBaseParameters | undefined>($baseData);
  const useDeliverySearchParams = () =>
    useStateSubscriber<RouteCalculatorSettings | null>($deliverySearchParams);
  const useRouteParams = () =>
    useStateSubscriber<RouteWidgetParams>($routeParams, () => $validation.next(true));
  const useIsConfigFromSearchParams = () => useStateSubscriber<boolean>($isConfigFromSearchParams);
  const useIsInit = () => useStateSubscriber<boolean>($isInit);
  const useIsNeedReloading = () => useStateSubscriber<boolean>($isNeedRoutesReloading);
  const useSettings = () => useStateSubscriber<Settings>($settings);
  const useAggregationData = () =>
    useStateSubscriber<AggregationData | undefined>($aggregationData);
  const useRoutes = () => useStateSubscriber<RouteWidgetResult>($routes);
  const useTerminals = () => useStateSubscriber<BranchItem[]>($terminals);
  const useStartTerminals = () => useStateSubscriber<BranchItem[]>($startTerminals);
  const useEndTerminals = () => useStateSubscriber<BranchItem[]>($endTerminals);
  const useFilterParameters = () => useStateSubscriber<FilterData>($filterParameters);
  const useCacheFilterParameters = () => useStateSubscriber<FilterData>($cacheFilterParameters);
  const useIsArchiveLoading = () => useStateSubscriber<boolean>($isArchiveLoading);
  const useIsMoreRoutesLoadingAvailable = () =>
    useStateSubscriber<boolean>($isMoreRoutesLoadingAvailable);
  const useIsRoutesLoading = () => useStateSubscriber<boolean>($isRoutesLoading);
  const useIsMoreRoutesLoading = () => useStateSubscriber<boolean>($isMoreRoutesLoading);
  const useSortData = () => useStateSubscriber<SortData>($sortData);
  const useLimit = () => useStateSubscriber<number>($limit);
  const useOffset = () => useStateSubscriber<number>($offset);
  const useValidation = () => useStateSubscriber<boolean>($validation);
  const useToBranch = () => useStateSubscriber<BranchItem | undefined>($toBranch);
  const useFromBranch = () => useStateSubscriber<BranchItem | undefined>($fromBranch);
  const useRouteSearchTaskId = () => useStateSubscriber<string | undefined>($routeSearchTaskId);
  const useIsTransportWithCustomWeightDiff = () =>
    useStateSubscriber<boolean>($isTransportWithCustomWeightDiff);
  const useIsStartSearchWithWeightDiff = () =>
    useStateSubscriber<boolean>($isStartSearchWithWeightDiff);
  const useRouteSearchTaskStatus = () =>
    useStateSubscriber<RouteSearchTaskStatus>($routeSearchTaskStatus);
  const useCalculationAbortController = () =>
    useStateSubscriber<AbortController>($calculationAbortController);
  const useIsEditCalculationParametersActive = () =>
    useStateSubscriber<boolean>($isEditCalculationParametersActive);

  return {
    useIsLoading,
    useValidation,
    useBaseDate,
    useDeliverySearchParams,
    useRouteParams,
    useIsConfigFromSearchParams,
    useIsInit,
    useIsNeedReloading,
    useSettings,
    useAggregationData,
    useFromBranch,
    useToBranch,
    useRoutes,
    useTerminals,
    useStartTerminals,
    useEndTerminals,
    useFilterParameters,
    useCacheFilterParameters,
    useIsArchiveLoading,
    useIsMoreRoutesLoadingAvailable,
    useIsRoutesLoading,
    useIsMoreRoutesLoading,
    useSortData,
    useLimit,
    useOffset,
    useRouteSearchTaskId,
    useRouteSearchTaskStatus,
    useCalculationAbortController,
    useIsEditCalculationParametersActive,
    useIsTransportWithCustomWeightDiff,
    useIsStartSearchWithWeightDiff,
    actions,
  };
};

export default useDelivery;
